<template>
  <mi-card class="search-summary">
    <!-- Title -->
    <mi-card-section class="search-summary__title selected-elements-padding">
      <h6 class="q-mt-none q-mb-none text-uppercase"> Selected Elements </h6>
      <mi-btn
        :ripple="false"
        class="summary-selection__clear-btn q-ml-auto"
        icon="trash"
        icon-size="18px"
        dense
        flat
        icon-type
        fab
        @click.stop="clearAllElements"
      >
        <mi-tooltip anchor="center left" self="center right"> Clear selection </mi-tooltip>
      </mi-btn>
    </mi-card-section>

    <mi-form ref="summaryForm" @submit="showCombinationsAsync()" @keydown.enter.prevent>
      <mi-infinite-scroll
        ref="miInfiniteScroll"
        class="mi-infinite-scroll"
      >
        <div v-if="hasCurrentCollection()" class="selected-elements-padding collection-name-wrapper">
          <div class="row">
            <span
              class="label-form text-subtitle2 text-weight-bold text-family-condensed q-mr-sm"
            >
              Collection
            </span>
            <div class="row">
              <span v-for="status in currentCollection?.status" :key="status" class="status-chip">
                {{ COLLECTION_STATUS_LABEL[status].label }}
              </span>
            </div>
          </div>
          <span class="collection-name-text">
            {{ currentCollection?.name }}
          </span>
        </div>

        <!-- Planning period range -->
        <mi-card-section class="selected-elements-padding">
          <span
            class="label-form text-subtitle2 text-weight-bold text-family-condensed"
          >
            Planning Period
          </span>
          <planning-period-range
            v-model:from="planningPeriodRange.from"
            v-model:to="planningPeriodRange.from"
            :disable-to="true"
            class="no-wrap q-mt-md planning-period-range q-pa-none"
            label-from=""
            label-to=""
          ></planning-period-range>
        </mi-card-section>

        <!-- Elements -->
        <mi-card-section class="search-summary__collections selected-elements-padding column no-wrap">
          <span class="label-form text-subtitle2 text-weight-bold text-family-condensed"> Structure Elements </span>

          <q-splitter
            v-model="splitterModel"
            horizontal
            unit="px"
            :limits="splitterLimits"
            class="splitter-container"
          >
            <template #before>
              <!-- Yellow collection -->
              <search-summary-collection
                :color="ELEMENT_COLORS.YELLOW"
                :items="selectedOptions"
                :total-sub-elements-counters="totalSubElementsCounters[ELEMENT_COLORS.YELLOW]"
                :are-all-options-loading="areAllOptionsLoading"
                @visible-items-count-updated="handleYellowVisibleItemsCountUpdate"
              >
                <template #header>
                  <search-summary-total-elements-label
                    label="Yellow collection"
                    :total-sub-elements="selectedChoicesCounter"
                    :total-elements="selectedOptions.length"
                    :world="ELEMENT_COLORS.YELLOW"
                  ></search-summary-total-elements-label>
                </template>
              </search-summary-collection>
            </template>

            <template #separator>
              <div class="splitter-separator">
                <button
                  type="button"
                  class="splitter-lines"
                  aria-label="Reset splitter size"
                  @mousedown="handleSplitterResizeStart"
                  @touchstart="handleSplitterResizeStart"
                  @dblclick.stop="setDefaultSplitSize"
                  @keydown.enter.stop.prevent="setDefaultSplitSize"
                  @keydown.space.stop.prevent="setDefaultSplitSize"
                >
                  <span></span>
                  <span></span>
                </button>
              </div>
            </template>

            <template #after>
              <!-- Green collection -->
              <search-summary-collection
                :color="ELEMENT_COLORS.GREEN"
                :items="selectedComponents"
                :total-sub-elements-counters="totalSubElementsCounters[ELEMENT_COLORS.GREEN]"
                :add-all-options="addAllOptions"
              >
                <template #header>
                  <search-summary-total-elements-label
                    label="Green collection"
                    :total-sub-elements="selectedKvsCounter"
                    :total-elements="selectedComponents.length"
                    :world="ELEMENT_COLORS.GREEN"
                  ></search-summary-total-elements-label>
                  <mi-btn
                    :ripple="false"
                    class="search-summary__collection-action-btn q-py-xs q-px-sm q-ml-xs"
                    flat
                    @click.stop="addAllOptions"
                  >
                    Add all options
                  </mi-btn>
                </template>
                <template #footer>
                  <div
                    v-if="selectedElements.components.length"
                    class="set-primary-le flex justify-between items-center"
                  >
                    <span class="text-subtitle2 text-weight-bold"> Set solution elements analysis </span>
                    <mi-switch
                      v-model="selectedForLE"
                      color="primary"
                      @click="handleSolutionElementsActivation"
                    >
                    </mi-switch>
                  </div>
                  <div
                    v-if="selectedForLE && selectedElements.components.length"
                    class="edit-btn-le"
                  >
                    <div class="flex justify-between">
                      <div class="edit-btn-le__content">
                        <div v-show="isLoadingSelectedComponents" class="edit-btn-le__content--loading">
                          <mi-spinner size="20px"></mi-spinner>
                          <span> components </span>
                        </div>
                        <span v-show="!isLoadingSelectedComponents">
                          {{ countSelectedObjects() }}/{{ selectedElementsForLes.length }} components
                        </span>
                      </div>
                      <div
                        class="text-weight-bold cursor-pointer"
                        role="button"
                        tabindex="0"
                        @click="handleEditSolutionsElements"
                      >
                        Edit
                      </div>
                    </div>
                    <div v-show="displayWarning" class="edit-btn-le__warning">
                      <div class="edit-btn-le__warning--icon">
                        <img src="@/assets/images/vm/warning-circle.svg" alt="warning-circle" width="16" height="16" />
                      </div>
                      <span class="edit-btn-le__warning--info">
                        LE list needs to be reviewed to the latest components addiction
                      </span>
                    </div>
                  </div>
                </template>
              </search-summary-collection>
            </template>
          </q-splitter>

          <div v-if="editSolutionElements" class="component-selection-wrapper mi-drawer-content-le">
            <div class="drawer-background"></div>
            <mi-drawer
              v-model="editSolutionElements"
              bordered
              overlay
              side="right"
              width="460"
            >
              <component-selection
                @close:edit-drawer="handleEditSolutionsElements"
              ></component-selection>
            </mi-drawer>
          </div>
        </mi-card-section>
      </mi-infinite-scroll>

      <!-- Actions -->
      <mi-card-actions class="no-wrap combination-submit-btn">
        <man-tooltip
          v-if="saveTheResultForLaterTooltip"
          id="saved-table-tooltip-id"
          class="saved-table-tooltip"
          :show="saveTheResultForLaterTooltip"
          target-selector=".saved-table-tooltip-target"
          content="“Show me later” is now available in the dropdown as “Save the result for later”"
          position="top"
        ></man-tooltip>
        <mi-btn
          class="text-no-wrap"
          :class="{ 'bg-accent-disabled' : areSelectedElementsEmpty }"
          color="accent"
          type="submit"
          :disable="areSelectedElementsEmpty"
        >
          <man-spinner
            v-if="areCombinationsLoading && showResultsLaterToggle"
            variant="accent"
            size="s"
          ></man-spinner>
          <span v-else>Show combinations</span>
        </mi-btn>
        <div class="saved-table-tooltip-target">
          <mi-btn-dropdown
            class="btn-dropdown"
            color="accent"
            :menu-offset="[0, 1]"
            :disable="areSelectedElementsEmpty"
          >
            <mi-list class="btn-save-as-collection-wrapper">
              <mi-list-item
                v-close-popup
                class="action-items"
                clickable
                @click="handleOpenSaveTheResultForLaterModal"
              >
                <img
                  src="@/assets/images/my_combinations_results.svg"
                  alt="show me results later icon"
                />
                Save the result for later
              </mi-list-item>
            </mi-list>
            <mi-list class="btn-save-as-collection-wrapper">
              <mi-list-item
                v-if="currentCollection?.status?.includes('CHANGED')"
                v-close-popup
                class="action-items"
                clickable
                @click="handleUpdateCollection"
              >
                <img src="@/assets/images/vm/reload-reverse.svg" alt="reload" />
                {{ actions.update.name }}
              </mi-list-item>
              <mi-list-item
                v-if="!hasCurrentCollection()"
                v-close-popup
                class="action-items"
                clickable
                @click="handleOpenModalSaveCollection"
              >
                <img src="@/assets/images/vm/add-new.svg" alt="add-new" />
                {{ actions.save.name }}
              </mi-list-item>
              <mi-list-item
                v-else
                v-close-popup
                class="action-items"
                clickable
                @click="handleOpenModalSaveCollection({ isSaveAsNew: true })"
              >
                <img src="@/assets/images/vm/add-new.svg" alt="add-new" />
                {{ actions.saveAsNew.name }}
              </mi-list-item>
            </mi-list>
          </mi-btn-dropdown>
        </div>
      </mi-card-actions>
    </mi-form>

    <div>
      <mi-overlay :model-value="areCombinationsLoading && !showResultsLaterToggle"></mi-overlay>
      <mi-notify :model-value="areCombinationsLoading && !showResultsLaterToggle">
        <template #inner-content>
          <div>
            <p class="q-ma-none text-weight-bold">
              {{ skipCombination ? 'Saving table.' : 'Table in progress.' }}
            </p>
            <mi-loader></mi-loader>
            <button v-if="!skipCombination" class="notification__btn" @click="handleSkipWaiting">
              Skip waiting
            </button>
          </div>
        </template>
      </mi-notify>
    </div>
  </mi-card>

  <product-model-change-dialog
    v-if="showProductModelChangeDialog"
    :is-same-product-model="true"
    :model-value="showProductModelChangeDialog"
    :modified-elements="modifiedElements"
    :close-dialog="closeDialog"
    @update:product-model="updateProductModelWithDetectedChanges"
  >
  </product-model-change-dialog>

  <save-collection-dialog
    :model-value="saveCollectionModal"
    :handle-close-modal="handelCloseModalSaveCollection"
    :handle-submit="handleSaveCollectionName"
    :save-collections-loading="saveCollectionsLoading"
    :should-reset="resetModal"
    :is-save-as-new="isSaveAsNew"
    :collection-name-to-be-edit="isSaveAsNew ?
      `Copy of ${ currentCollection?.name }` : currentCollection?.name"
  >
  </save-collection-dialog>

  <save-result-for-later-dialog
    :model-value="showSaveTheResultForLaterModal"
    :handle-close-modal="() => showSaveTheResultForLaterModal = false"
    :handle-submit="handleSaveTheResultForLaterAndTriggerShowCombinations"
  >
  </save-result-for-later-dialog>

  <mi-inner-loading :showing="updatingCollectionLoading">
    <mi-spinner size="70px"></mi-spinner>
    <h6 class="q-mt-lg q-mb-none"> Updating collection to new setup.... </h6>
  </mi-inner-loading>
</template>

<script>
  import throttle from 'lodash.throttle'
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

  import {
    applyVariantTreeRulesAsync,
    getComponentOptions,
    postSummary,
    getAppliedVariantTreeRules,
    saveVariantTreeCombination,
    getComponentVariantOptions,
    postCollections,
    searchOptions,
    searchComponents
  } from '@/api'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import {
    ELEMENT_COLORS,
    SEARCH_RESULT_TYPES,
    CURRENT_SELECTION_SAVE_THROTTLE_INTERVAL,
    PM_TYPE,
    ASYNC_REQUEST_STATUS
  } from '@/constants'
  import {
    TOGGLE_EDIT_SOLUTION_ELEMENTS_STATE,
    UPDATE_SELECTED_ELEMENTS,
    SET_ELEMENTS_LOADING_STATE,
    SET_COLLECTIONS_SAVING,
    CLEAR_SELECTED_ELEMENTS
  } from '@/store/modules/search/mutationTypes'
  import { COMBINATIONS_ROUTE, SAVED_COLLECTIONS_ROUTE, PRODUCT_MODELS_ROUTE } from '@/router/routeNames'
  import { lastRoute } from '@/router'
  import * as analyticsConstants from '@/utils/analytics/constants'

  import PlanningPeriodRange from '@/components/base/PlanningPeriodRange.vue'
  import {
    MiCard,
    MiSpinner,
    MiCardActions,
    MiCardSection
  } from '@/packages/@mi-library'
  import setRecordAnalyticsTrace, { isFlowCompletedPerSession } from '@/utils/analytics/recordAnalyticsTrace'
  import { getTimeSpent } from '@/utils/timeSpent'
  import emitFlowState from '@/utils/custom-events/customEventsEmit'
  import { poll } from '@/api/polling'
  import notify from '@/utils/notify'
  import ComponentSelection from '@/components/combinations/component-selection/ComponentSelection.vue'
  import MiBtnDropdown from '@/packages/@mi-library/MiBtnDropdown/MiBtnDropdown.vue'
  import MiListItem from '@/packages/@mi-library/MiList/MiListItem.vue'
  import { BASIC_INFO } from '@/plugins/quasar/notifications/notificationTypes'
  import { maxLength } from '@/utils/validators'
  import SaveCollectionDialog from '@/components/search/search-summary/save-collection-dialog/SaveCollectionDialog.vue'
  import { base64EncodeUnicode } from '@/utils/base64'
  import SaveResultForLaterDialog
    from '@/components/search/search-summary/save-result-for-later-dialog/SaveResultForLaterDialog.vue'
  import SearchSummaryCollection from './SearchSummaryCollection.vue'
  import SearchSummaryTotalElementsLabel from './SearchSummaryTotalElementsLabel.vue'
  import ProductModelChangeDialog from '../../product-model/ProductModelChangeDialog.vue'
  import MiInfiniteScroll from '../../../packages/@mi-library/MiInfiniteScroll/MiInfiniteScroll.vue'
  import { COLLECTION_STATUS_LABEL } from '../../../../tests/unit/__mocks__/data'
  import * as notificationTypes from '../../../plugins/quasar/notifications/notificationTypes'
  import { updateCollectionData } from '../../../api/rest/saved-collections'
  import { useSearchSummarySplitter } from './composables/useSearchSummarySplitter'

  export default {
    name: 'SearchSummary',
    components: {
      SaveResultForLaterDialog,
      MiInfiniteScroll,
      MiListItem,
      MiBtnDropdown,
      ProductModelChangeDialog,
      PlanningPeriodRange,
      SearchSummaryCollection,
      SearchSummaryTotalElementsLabel,
      MiCard,
      MiSpinner,
      MiCardActions,
      MiCardSection,
      ComponentSelection,
      SaveCollectionDialog
    },
    setup() {
      return {
        ...useSearchSummarySplitter()
      }
    },
    data: () => ({
      ELEMENT_COLORS,
      COLLECTION_STATUS_LABEL,
      correlationIdToBeSaved: null,
      skipCombination: false,
      saveTheResultForLaterTooltip: false,
      saveTheResultForLaterTooltipTimeout: null,
      planningPeriodRange: {
        from: '',
        to: ''
      },
      selectedChoicesCounter: 0,
      selectedKvsCounter: 0,
      showSaveTheResultForLaterModal: false,
      hasShownSaveTheResultForLaterTooltipOnce: false,
      totalSubElementsCounters: {
        [ELEMENT_COLORS.YELLOW]: [],
        [ELEMENT_COLORS.GREEN]: []
      },
      isSaveAsNew: false,
      currentCollection: null,
      maxLength,
      jobInputName: '',
      sentFromSavedCollections: lastRoute?.value?.fullPath === SAVED_COLLECTIONS_ROUTE.path,
      sentFromProductModelDetails: lastRoute?.value?.fullPath === PRODUCT_MODELS_ROUTE.path,
      areAllOptionsLoading: false,
      clearPoll: null,
      selectedForLE: false,
      showResultsLaterToggle: false,
      editSolutionElements: false,
      isLoadingSelectedComponents: false,
      displayWarning: false,
      showProductModelChangeDialog: false,
      modifiedElements: {},
      updatingCollectionLoading: false,
      actions: {
        save: {
          name: 'Save as collection',
          icon: '@/assets/images/vm/add-new.svg',
          type: 'save'
        },
        saveAsNew: {
          name: 'Save as new collection',
          icon: '@/assets/images/vm/add-new.svg',
          type: 'save_new'
        },
        update: {
          name: 'Update collection',
          icon: '@/assets/images/vm/add-new.svg',
          type: 'update'
        }
      },
      saveCollectionModal: false,
      saveCollectionsLoading: false,
      resetModal: false
    }),

    computed: {
      ...mapState(['user']),
      ...mapGetters('search', [
        'areSelectedElementsEmpty',
        'currentSelectedElementsIdsLength',
        'selectedOptions',
        'selectedComponents',
        'selectedElements',
        'selectedElementsForLes'
      ]),
      ...mapState('product-model', ['selectedSearchProductModel']),
      ...mapState('search', ['showResultsLater', 'areCombinationsLoading']),
      ...mapGetters('product-model', ['selectedProductModel', 'hasSelectedSearchProductModel'])
    },

    watch: {
      async correlationIdToBeSaved() {
        if (this.correlationIdToBeSaved) {
          await saveVariantTreeCombination({ correlationId: this.correlationIdToBeSaved })
          this.setCombinationsLoading({ areCombinationsLoading: false })
          this.tableIsInProgressNotify()
        }
      },
      currentSelectedElementsIdsLength() {
        if (this.hasCurrentCollection()) {
          this.updateCollectionChangedStatus()
        }

        if (
          this.selectedProductModel.encodedBusinessName
          && !this.sentFromSavedCollections
        ) {
          this.saveCurrentSelection()
        }

        this.sentFromSavedCollections = false
      },
      selectedElements() {
        const allSelected = this.countSelectedObjects() === this.selectedElementsForLes.length
        this.displayWarning = !allSelected && this.selectedForLE
      },
      planningPeriodRange: {
        handler(value) {
          if (this.hasCurrentCollection() && value.from !== value.to) {
            this.updateCollectionChangedStatus()

            this.currentCollection.planningPeriodRange.from = value.from
            this.currentCollection.planningPeriodRange.to = value.from

            this.saveCurrentSelection()
          }
          else {
            this.$q.localStorage.set(
              process.env.VUE_APP_STORAGE_KEY_PLANNING_PERIOD,
              { from: value.from, to: value.to }
            )
          }
        },
        deep: true
      },
      selectedOptions: {
        async handler(selectedOptions) {
          this.selectedChoicesCounter = this.getSelectedSubElementsCount(selectedOptions, 'choices')
          await this.fetchMissingCountersIfPossible()

          if (!this.hasShownSaveTheResultForLaterTooltipOnce) {
            this.showSaveForLaterTooltip()
            this.hasShownSaveTheResultForLaterTooltipOnce = true
          }
        },
        deep: true
      },
      selectedComponents: {
        async handler(selectedComponents) {
          this.selectedKvsCounter = this.getSelectedSubElementsCount(selectedComponents, 'componentVariants')
          await this.fetchMissingCountersIfPossible()

          if (!this.hasShownSaveTheResultForLaterTooltipOnce) {
            this.showSaveForLaterTooltip()
            this.hasShownSaveTheResultForLaterTooltipOnce = true
          }
        },
        deep: true
      },
      selectedSearchProductModel: {
        async handler() {
          this.totalSubElementsCounters = {
            [ELEMENT_COLORS.YELLOW]: [],
            [ELEMENT_COLORS.GREEN]: []
          }

          await this.fetchMissingCountersIfPossible()
        },
        deep: true
      }
    },

    async created() {
      this.SET_ELEMENTS_LOADING_STATE(true)
      try {
        await this.getCurrentSelectionValues()
        this.setPlanningPeriodFromLocalStorageOrCurrentCollection()

        if (this.currentSelectedElementsIdsLength > 0) {
          this.showSaveForLaterTooltip()
          this.hasShownSaveTheResultForLaterTooltipOnce = true
        }
      }
      finally {
        this.SET_ELEMENTS_LOADING_STATE(false)
      }
    },
    beforeUnmount() {
      this.clearPoll?.()
      this.setResultsForLater({ showResultsLater: false })
      this.setCombinationsLoading({ areCombinationsLoading: false })

      clearTimeout(this.saveTheResultForLaterTooltipTimeout)
    },
    methods: {
      ...mapMutations('search', {
        UPDATE_SELECTED_ELEMENTS,
        TOGGLE_EDIT_SOLUTION_ELEMENTS_STATE,
        SET_ELEMENTS_LOADING_STATE,
        SET_COLLECTIONS_SAVING,
        CLEAR_SELECTED_ELEMENTS
      }),
      ...mapActions('search', [
        'getCurrentSelection',
        'setComponentsForLE',
        'setResultsForLater',
        'setCombinationsLoading',
        'updateListIfElementIdChange'
      ]),
      showSaveForLaterTooltip() {
        clearTimeout(this.saveTheResultForLaterTooltipTimeout)

        this.saveTheResultForLaterTooltip = true

        this.saveTheResultForLaterTooltipTimeout = setTimeout(() => {
          this.saveTheResultForLaterTooltip = false
        }, 5000)
      },
      hasProductModelReady() {
        return Boolean(
          this.selectedSearchProductModel?.productModelType
            && (
              this.selectedSearchProductModel.productModelType === PM_TYPE.USER_EXPORTS
                ? this.selectedSearchProductModel.id
                : this.selectedSearchProductModel.encodedBusinessName
            )
        )
      },
      handleOpenSaveTheResultForLaterModal() {
        this.showSaveTheResultForLaterModal = true
      },
      handleSaveTheResultForLaterAndTriggerShowCombinations(tableName) {
        this.showSaveTheResultForLaterModal = false
        this.jobInputName = tableName
        this.showResultsLaterToggle = true
        this.showCombinationsAsync()
      },
      getSelectedSubElementsCount(elements = [], subElementsKey = '') {
        // eslint-disable-next-line security/detect-object-injection
        return elements.reduce((total, element) => total + (element[subElementsKey]?.length || 0), 0)
      },
      getCounterById2(color, id2) {
        // eslint-disable-next-line security/detect-object-injection
        return this.totalSubElementsCounters[color].find(counter => counter.id2 === id2)
      },
      getMissingIds2(items = [], color) {
        return items
          .filter(item => item.id2 && !this.getCounterById2(color, item.id2))
          .map(item => item.id2)
      },
      mergeCounters(color, counters = []) {
        // eslint-disable-next-line security/detect-object-injection
        const existingCounters = this.totalSubElementsCounters[color]
        const mergedCounters = [...existingCounters]

        counters.forEach(counter => {
          const existingIndex = mergedCounters.findIndex(item => item.id2 === counter.id2)

          if (existingIndex >= 0) {
            // eslint-disable-next-line security/detect-object-injection
            mergedCounters[existingIndex] = counter
          }
          else {
            mergedCounters.push(counter)
          }
        })

        this.totalSubElementsCounters = {
          ...this.totalSubElementsCounters,
          [color]: mergedCounters
        }
      },
      formatSearchQueryParam(searchStr = '') {
        return searchStr
          .split(';')
          .map(item => base64EncodeUnicode(item.trim()))
          .filter(item => item.length)
          .join(',') || ''
      },
      getProductModelSearchParams(query = '') {
        if (this.selectedSearchProductModel.productModelType === PM_TYPE.USER_EXPORTS) {
          return {
            query,
            pmIdentifier: this.selectedSearchProductModel.id,
            pmType: this.selectedSearchProductModel.productModelType || PM_TYPE.PRODUCT_MODEL
          }
        }

        return {
          query,
          pmIdentifier: this.selectedSearchProductModel.encodedBusinessName,
          pmType: this.selectedSearchProductModel.productModelType || PM_TYPE.PRODUCT_MODEL
        }
      },
      async fetchMissingCountersIfPossible() {
        if (!this.hasProductModelReady()) return

        await this.fetchMissingCounters({
          items: this.selectedOptions,
          color: ELEMENT_COLORS.YELLOW,
          searchFn: searchOptions,
          subElementsKey: 'choices'
        })

        await this.fetchMissingCounters({
          items: this.selectedComponents,
          color: ELEMENT_COLORS.GREEN,
          searchFn: searchComponents,
          subElementsKey: 'componentVariants'
        })
      },
      async fetchMissingCounters({ items = [], color = '', searchFn, subElementsKey = '' }) {
        const ids2 = this.getMissingIds2(items, color)

        if (!ids2.length) return

        const response = await searchFn({
          ...this.getProductModelSearchParams(
            this.formatSearchQueryParam(ids2.join(';'))
          ),
          numberOfElements: -1
        })

        const counters = (response?.elements || []).map(item => ({
          id2: item.id2,
          // eslint-disable-next-line security/detect-object-injection
          totalSubElements: item[subElementsKey]?.length || 0
        }))

        this.mergeCounters(color, counters)
      },
      async handleSkipWaiting() {
        try {
          if (this.clearPoll) {
            this.skipCombination = true
            this.clearPoll()

            // Analytics
            recordAnalytics(analyticsConstants.VM_SKIP_WAITING)
          }
        }
        catch (err) {
          throw new Error(
            `Error trying to save the combination into the saved tables: ${ this.correlationIdToBeSaved }`
          )
        }
      },
      closeDialog() {
        this.showProductModelChangeDialog = false
      },
      countSelectedObjects() {
        return this.selectedElementsForLes
          .reduce((count, component) => count + (component.selectedForLE === true ? 1 : 0), 0)
      },
      handleEditSolutionsElements(content) {
        this.TOGGLE_EDIT_SOLUTION_ELEMENTS_STATE()
        this.editSolutionElements = !this.editSolutionElements

        if (content && content.confirmed) {
          this.isLoadingSelectedComponents = true

          setTimeout(() => {
            this.isLoadingSelectedComponents = false
            notify({
              content: 'Solution elements updated.',
              type: 'dark_info',
              position: 'bottom-right',
              progress: false,
              timeout: 2000
            }, true)
          }, 2000)
        }

        this.displayWarning = false
      },
      handleSolutionElementsActivation() {
        this.setComponentsForLE({ selectedForLE: this.selectedForLE })
      },
      handleShowResultsLaterActivation() {
        this.setResultsForLater({ showResultsLater: this.showResultsLaterToggle })
        this.jobInputName = ''

        if (this.showResultsLaterToggle) {
          recordAnalytics(analyticsConstants.VM_SAVED_FOR_LATER_CALCULATION)
        }
      },
      async addAllOptions({ singleComponentVariant = false, componentVariantId = '' }) {
        const isFormValid = await this.$refs?.summaryForm?.validate?.()

        if (!(this.selectedComponents.length && isFormValid)) return

        this.areAllOptionsLoading = true
        let options = []

        try {
          if (singleComponentVariant) {
            const result = await getComponentVariantOptions({
              id: componentVariantId,
              pmEncodedBusinessName: this.selectedSearchProductModel.encodedBusinessName,
              fromPeriod: this.planningPeriodRange.from,
              toPeriod: this.planningPeriodRange.from
            })
            options = result.tarOptions
          }
          else {
            const ids = this.selectedComponents.map(({ id } = {}) => id).join()

            options = await getComponentOptions({
              ids,
              pmType: this.selectedSearchProductModel.productModelType,
              pmIdentifier: this.selectedSearchProductModel.productModelType === PM_TYPE.PRODUCT_MODEL
                ? this.selectedSearchProductModel.encodedBusinessName
                : this.selectedSearchProductModel.id,
              fromPeriod: this.planningPeriodRange.from,
              toPeriod: this.planningPeriodRange.from
            })
          }

          this.UPDATE_SELECTED_ELEMENTS({
            elementsType: SEARCH_RESULT_TYPES.OPTIONS,
            subElementsType: SEARCH_RESULT_TYPES.CHOICES,
            elements: options
          })

          recordAnalytics(analyticsConstants.VM_ADDED_ALL_OPTIONS)
        }
        finally {
          this.areAllOptionsLoading = false
        }
      },
      tableIsInProgressNotify() {
        notify({
          content: 'Table is in progress in “Saved Tables”.',
          type: BASIC_INFO,
          position: 'bottom'
        })
      },
      async showCombinationsAsync() {
        try {
          this.setCombinationsLoading({ areCombinationsLoading: true })

          const { executePoll, clearPoll } = poll()
          this.clearPoll = clearPoll

          const { status, correlationId } = await executePoll({
            correlationIdEndpoint: {
              getCorrelationId: applyVariantTreeRulesAsync,
              params: {
                options: this.selectedOptions,
                components: this.selectedComponents,
                fromPeriod: this.planningPeriodRange.from,
                toPeriod: this.planningPeriodRange.from,
                pmIdentifier:
                  this.selectedSearchProductModel.productModelType === PM_TYPE.PRODUCT_MODEL
                    ? this.selectedSearchProductModel.encodedBusinessName
                    : this.selectedSearchProductModel.id,
                pmType: this.selectedSearchProductModel.productModelType,
                showLater: this.showResultsLaterToggle,
                name: this.jobInputName
              }
            },
            asyncEndpoint: getAppliedVariantTreeRules,
            ignorePolling: this.showResultsLaterToggle
          })

          const skippedCombination = status === ASYNC_REQUEST_STATUS.STOPPED
            && this.skipCombination && !this.showResultsLaterToggle

          if (skippedCombination) this.correlationIdToBeSaved = correlationId

          const isShowResultsLaterActivated = this.showResultsLaterToggle && status === ASYNC_REQUEST_STATUS.STOPPED

          if (isShowResultsLaterActivated) {
            recordAnalytics(analyticsConstants.SHOW_ME_LATER_TOGGLE_AND_SHOW_COMBINATIONS)
            this.tableIsInProgressNotify()
            return
          }

          emitFlowState('search', 'end')

          if (status === ASYNC_REQUEST_STATUS.SUCCESS && correlationId) {
            let query = { correlationId }
            query = this.extraQueryParams(query)

            await this.$router.push({ name: COMBINATIONS_ROUTE.name, query })

            // Analytics
            setRecordAnalyticsTrace(analyticsConstants.SHOW_COMBINATIONS)
            recordAnalytics(analyticsConstants.VM_SHOW_COMBINATIONS)
            if (isFlowCompletedPerSession(analyticsConstants.VARIANT_MANAGEMENT_COMPLETED_FLOW)) {
              recordAnalytics(analyticsConstants.VM_COMPLETED_FLOW)
              recordAnalytics(
                analyticsConstants.VM_COMPLETE_FLOW_TIME_SPENT,
                { timeSpent: getTimeSpent() }
              )
            }
          }
        }
        catch (e) {
          notify({
            title: `Error ${ e.status || '' }`,
            content: 'Show combinations',
            type: 'negative'
          })
        }
        finally {
          this.setCombinationsLoading({ areCombinationsLoading: false })
          this.jobInputName = ''
          this.skipCombination = false
        }
      },
      getOldProductModel() {
        const productModel = this.$q.localStorage.getItem(process.env.VUE_APP_STORAGE_KEY_PRODUCT_MODEL)
        const vmProductModel = this.$q.localStorage.getItem(process.env.VUE_APP_STORAGE_KEY_VM_PRODUCT_MODEL)

        return productModel || vmProductModel
      },
      async getCurrentSelectionValues(collectionOverride = null) {
        if (collectionOverride) {
          this.currentCollection = collectionOverride
          return
        }

        const {
          modifiedElements: updatedElements,
          options,
          components,
          pmEncodedBusinessName,
          collection
        } = await this.getCurrentSelection()

        const oldPm = this.getOldProductModel()

        if (oldPm.encodedBusinessName === pmEncodedBusinessName) {
          if (updatedElements?.modifiedComponents.length || updatedElements?.modifiedOptions.length) {
            this.modifiedElements = {
              options: updatedElements.modifiedOptions,
              components: updatedElements.modifiedComponents,
              completeOptions: options,
              completeComponents: components,
              pmIdentifier: oldPm.businessName
            }
            this.showProductModelChangeDialog = true
          }
        }

        this.currentCollection = collection
      },
      async updateProductModelWithDetectedChanges({ detectElementsData, saveSummary = true }) {
        const { encodedBusinessName, productModelType, onDemandExportMetadata, id } = this.selectedSearchProductModel

        this.updateListIfElementIdChange({ detectElementsData })

        saveSummary && await postSummary({
          name: `${ this.user.username }_CurrentSelection`,
          pmEncodedBusinessName: productModelType === 'USER_EXPORTS'
            ? btoa(onDemandExportMetadata.exportName)
            : encodedBusinessName,
          productModelId: id,
          pmType: productModelType,
          options: this.selectedOptions,
          components: this.selectedComponents,
          currentSelection: true,
          collection: this.hasCurrentCollection() ? this.currentCollection : null
        })
      },
      saveCurrentSelection: throttle(function postCurrentSummary() {
        const name = `${ this.user.username }_CurrentSelection`

        const selectedComponentsWithoutLeFlag = this.selectedComponents?.map(
          ({ selectedForLE, ...attrs }) => attrs
        )

        postSummary({
          name,
          pmType: this.selectedSearchProductModel.productModelType,
          pmEncodedBusinessName: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS'
            ? btoa(this.selectedSearchProductModel.onDemandExportMetadata.exportName)
            : this.selectedSearchProductModel.encodedBusinessName,
          productModelId: this.selectedSearchProductModel.id,
          options: this.selectedOptions,
          components: selectedComponentsWithoutLeFlag,
          currentSelection: true,
          collection: this.hasCurrentCollection() ? this.currentCollection : null
        })
      }, CURRENT_SELECTION_SAVE_THROTTLE_INTERVAL),
      setPlanningPeriodFromLocalStorageOrCurrentCollection() {
        const planningPeriod = this.hasCurrentCollection()
          ? this.currentCollection.planningPeriodRange
          : this.$q.localStorage.getItem(
            process.env.VUE_APP_STORAGE_KEY_PLANNING_PERIOD
          )

        if (planningPeriod) {
          this.planningPeriodRange = {
            from: planningPeriod.from || '',
            to: planningPeriod.to || ''
          }
        }
      },
      extraQueryParams(query) {
        let queryWithParams = query
        const pp = this.getPlanningPeriod()
        const { productModelType: pmType, displayName } = this.selectedSearchProductModel
        if (pp.length) queryWithParams = { ...queryWithParams, pp }
        const productModelBusinessName = this.getProductModelBusinessName()
        if (productModelBusinessName.length) queryWithParams = { ...queryWithParams, productModelBusinessName }
        const exportName = pmType === PM_TYPE.USER_EXPORTS && displayName
        if (exportName) queryWithParams = { ...queryWithParams, exportName }
        return queryWithParams
      },
      getPlanningPeriod() {
        let pp = ''
        if ((this.planningPeriodRange.from && this.planningPeriodRange.to).length) {
          pp = `${ this.planningPeriodRange.from } - ${ this.planningPeriodRange.to }`
        }
        if ((this.planningPeriodRange.from).length) {
          pp = this.planningPeriodRange.from
        }
        return btoa(pp)
      },
      getProductModelBusinessName() {
        return btoa(this.selectedSearchProductModel.businessName ?? '')
      },
      handleOpenModalSaveCollection({ isSaveAsNew = false }) {
        this.isSaveAsNew = isSaveAsNew
        this.saveCollectionModal = true
      },
      async handleUpdateCollection() {
        this.updatingCollectionLoading = true
        try {
          await updateCollectionData({
            collectionId: this.currentCollection.id,
            pmEncodedBusinessName: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS'
              ? btoa(this.selectedSearchProductModel.onDemandExportMetadata.exportName)
              : this.selectedSearchProductModel.encodedBusinessName,
            isUserExports: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS',
            options: this.selectedOptions,
            components: this.selectedComponents,
            planningPeriodRange: {
              from: this.planningPeriodRange.from,
              to: this.planningPeriodRange.from
            },
            productModelId: this.selectedSearchProductModel.id
          })

          this.currentCollection.status[1] = COLLECTION_STATUS_LABEL.UPDATED.key

          await postSummary({
            name: `${ this.user.username }_CurrentSelection`,
            pmEncodedBusinessName: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS'
              ? btoa(this.selectedSearchProductModel.onDemandExportMetadata.exportName)
              : this.selectedSearchProductModel.encodedBusinessName,
            productModelId: this.selectedSearchProductModel.id,
            pmType: this.selectedSearchProductModel.productModelType,
            options: this.selectedOptions,
            components: this.selectedComponents,
            currentSelection: true,
            collection: this.currentCollection
          })

          notify({
            content: 'New collection setup updated.',
            type: notificationTypes.BASIC_INFO,
            position: 'bottom',
            progress: false,
            closeIcon: false,
            timeout: 2000
          })

          this.SET_COLLECTIONS_SAVING({ areCollectionsSaved: true })
        }
        catch (error) {
          console.error('Error updating collection:', error)
        }
        finally {
          this.updatingCollectionLoading = false
        }
      },
      handelCloseModalSaveCollection() {
        this.saveCollectionModal = false
      },
      updateCollectionChangedStatus() {
        if (!(this.currentCollection.status.length > 1)) {
          this.currentCollection.status?.push(COLLECTION_STATUS_LABEL.CHANGED.key)
        }
        else {
          this.currentCollection.status[1] = COLLECTION_STATUS_LABEL.CHANGED.key
        }
      },
      async handleSaveCollectionName(name) {
        this.saveCollectionsLoading = true
        try {
          const selectedComponentsWithoutLeFlag = this.selectedComponents?.map(
            ({ selectedForLE, ...attrs }) => attrs
          )

          const { id } = await postCollections(({
            name,
            productModelId: this.selectedSearchProductModel?.id,
            pmType: this.selectedSearchProductModel.productModelType,
            isUserExports: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS',
            pmEncodedBusinessName: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS'
              ? btoa(this.selectedSearchProductModel.onDemandExportMetadata.exportName)
              : this.selectedSearchProductModel.encodedBusinessName,
            options: this.selectedOptions,
            components: selectedComponentsWithoutLeFlag,
            currentSelection: true,
            planningPeriodRange: {
              from: this.planningPeriodRange.from,
              to: this.planningPeriodRange.from
            }
          }))

          await postSummary({
            name: `${ this.user.username }_CurrentSelection`,
            pmEncodedBusinessName: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS'
              ? btoa(this.selectedSearchProductModel.onDemandExportMetadata.exportName)
              : this.selectedSearchProductModel.encodedBusinessName,
            productModelId: this.selectedSearchProductModel.id,
            pmType: this.selectedSearchProductModel.productModelType,
            options: this.selectedOptions,
            components: selectedComponentsWithoutLeFlag,
            currentSelection: true,
            collection: {
              id,
              name,
              planningPeriodRange: {
                from: this.planningPeriodRange.from,
                to: this.planningPeriodRange.from
              },
              status: [COLLECTION_STATUS_LABEL.VIEWING.key]
            }
          })

          await this.getCurrentSelectionValues()

          this.SET_COLLECTIONS_SAVING({ areCollectionsSaved: true })

          // Success handling
          notify({
            content: 'Saved! Available in Saved collections.',
            type: notificationTypes.BASIC_INFO,
            position: 'bottom',
            progress: false,
            closeIcon: false,
            timeout: 2000
          })
        }
        catch (error) {
          // Error handling
          notify({
            content: 'Could not be saved pls try again',
            type: 'dark_info',
            position: 'bottom',
            progress: false,
            timeout: 2000
          }, true)
          console.error('Error saving collection:', error)
        }
        finally {
          this.saveCollectionModal = false
          this.saveCollectionsLoading = false
        }
      },
      hasCurrentCollection() {
        return this.currentCollection !== null
      },
      async clearAllElements() {
        this.CLEAR_SELECTED_ELEMENTS(SEARCH_RESULT_TYPES.OPTIONS)
        this.CLEAR_SELECTED_ELEMENTS(SEARCH_RESULT_TYPES.COMPONENTS)
        this.selectedForLE = false
        this.showResultsLaterToggle = false
        this.jobInputName = ''

        this.currentCollection = null
        this.planningPeriodRange = {
          from: '',
          to: ''
        }

        await postSummary({
          name: `${ this.user.username }_CurrentSelection`,
          pmEncodedBusinessName: this.selectedSearchProductModel.productModelType === 'USER_EXPORTS'
            ? btoa(this.selectedSearchProductModel.onDemandExportMetadata.exportName)
            : this.selectedSearchProductModel.encodedBusinessName,
          productModelId: this.selectedSearchProductModel.id,
          pmType: this.selectedSearchProductModel.productModelType,
          options: [],
          components: [],
          currentSelection: true,
          collection: null
        })

        this.resetModal = true

        this.$nextTick(() => {
          this.resetModal = false
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  $summary-total-height: 300px;
  $collections-space-height: 60px;

  .status-chip {
    background-color: #eaedf3;
    width: 50px;
    height: 16px;
    border-radius: 24px;
    font-size: 10px;
    font-weight: 400;
    padding: 0 4px;
    line-height: 100%;
    margin-right: 5px;
    place-content: center;
    text-align-last: center;
  }

  .planning-period-range {
    padding-bottom: 0;

    ::v-deep(.mi-field) {
      padding-top: 0;
    }

    ::v-deep(.mi-field.q-field .q-field__bottom) {
      padding-bottom: 0;
    }
  }

  .save-for-later-input .q-field {
    padding-bottom: 0;
  }

  .label-form {
    color: $mi-anthracite-800;
    font-feature-settings: "clig" off, "liga" off;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 14px;
  }

  .mi-drawer-content-le {
    .q-drawer {
      /* stylelint-disable declaration-no-important */
      top: 60px !important;
    }

    ::v-deep(.q-drawer) {
      width: 475px !important;
    }
  }

  @media (max-width: $mi-responsive-medium-width) {
    .mi-drawer-content-le {
      ::v-deep(.q-drawer) {
        width: 100vw !important;
      }
    }
  }

  .search-summary {
    max-height: 100%;
    max-width: 474px;
    min-width: 370px;

    &__collection-action-btn {
      font-size: 10px;
      text-decoration: underline;
    }

    &__title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .summary-collection {
    margin-top: 8px;
  }

  .selected-elements-padding {
    padding: 8px 24px 0 16px;
  }

  .set-primary-le {
    padding: 6px 16px;
  }

  .edit-btn-le {
    padding: 6px 16px 8px;

    &__content {
      &--loading {
        color: $mi-silver-700;
      }
    }

    &__warning {
      display: flex;
      height: 32px;
      padding: 10px;
      align-items: center;
      gap: 10px;
      align-self: stretch;
      margin-top: 4%;
      border: 1px solid var(--color-semantic-warning-bold, $mi-yellow-900);
      background: var(--color-background-surface, $mi-white);

      &--info {
        color: var(--color-semantic-warning-bold, $mi-yellow-900);
        font-family: $mi-typography-font-family;
        font-size: 11px;
        font-style: normal;
        font-weight: 700;
        line-height: 130%;
      }
    }
  }

  .component-selection-wrapper {
    z-index: 100;
  }

  .q-card__actions .q-btn {
    width: 100%;
  }

  .combination-submit-btn {
    padding: 12px 24px 24px 16px;
    display: flex;
    flex-direction: row;
    gap: 2px;
  }

  .multi-folder-icon {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
  }

  .notification {
    &__btn {
      background-color: transparent;
      border: 0;
      cursor: pointer;
      color: $mi-white;
      font-size: 14px;
      font-weight: 400;
      margin-top: 16px;
      line-height: 21px;
      text-decoration: underline;
    }

    &__btn:hover {
      color: $mi-anthracite-300;
    }
  }

  .btn-dropdown {
    width: 40px !important;
    height: 40px;
    display: flex;
    align-items: center;
    margin: 0 !important;
  }

  .btn-save-as-collection-wrapper {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: fit-content;
    flex-direction: column;
    max-height: 100%;
  }

  .action-items {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
  }

  .collection-name-wrapper {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .collection-name-text {
    font-size: 14px;
    font-weight: 400;
    color: #303c49;
    word-break: break-all;
  }

  .mi-infinite-scroll {
    overflow: auto;
    max-height: calc(100vh - 180px);
    overflow-y: scroll;
    padding-bottom: 1px;
  }

  .mi-icon.on-left {
    margin-right: 0 !important;
  }

  .splitter-container {
    height: calc(100vh - 300px);
  }

  .saved-table-tooltip {
    margin-bottom: 20px !important;
  }

  ::v-deep(.splitter-lines) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: row-resize;
    height: 20px;
  }

  ::v-deep(.q-splitter--horizontal > .q-splitter__separator) {
    height: 10px;
    background-color: $mi-white;
  }

  ::v-deep(.q-splitter__panel) {
    overflow-y: hidden;
  }

  ::v-deep(.splitter-lines span) {
    display: block;
    width: 50px;
    height: 3px;
    border-radius: 999px;
    background: $mi-silver-700;
  }
</style>
